/* eslint-disable camelcase */
import {
  useMutation,
  UseMutationOptions,
  UseMutationResult,
  useQuery,
  UseQueryOptions,
  UseQueryResult,
} from '@tanstack/react-query';

import AmplifyApiService, { PaginatedResult } from '../../services/AmplifyApiService';
import IAmplifyApiConfig from '../../shared/interfaces/IAmplifyApiConfig';
import { INotification } from '../../shared/interfaces/INotification';

const amplifyApiService = new AmplifyApiService();

const BASE_PATH = 'notifications';

export type NotificationsParams = {
  read?: boolean;
  size?: number;
  skip?: number;
};

export const useListNotifications = (
  filters: NotificationsParams = {},
  options: UseQueryOptions<PaginatedResult<INotification[]>, Error> = {},
): UseQueryResult<PaginatedResult<INotification[]>> => {
  const queryParams: { [key: string]: string } = {};

  if (filters.read != null) {
    queryParams.read = filters.read.toString();
  }

  if (filters.size != null) {
    queryParams.size = filters.size.toString();
  }

  if (filters.skip != null) {
    queryParams.skip = filters.skip.toString();
  }

  const amplifyApiConfig: IAmplifyApiConfig = {
    apiName: 'PacificApi',
    path: BASE_PATH,
    options: {
      queryParams,
    },
  };

  const query = useQuery<PaginatedResult<INotification[]>, Error>({
    queryKey: ['notifications', filters],
    queryFn: async (): Promise<PaginatedResult<INotification[]>> => {
      const response = await amplifyApiService.paginatedGet<INotification[]>(amplifyApiConfig);

      return response;
    },
    cacheTime: 0, // Doesn't cache any data
    ...options,
  });

  return query;
};

export const useUpdateNotification = (
  options: UseMutationOptions<void, Error, number> = {},
): UseMutationResult<void, Error, number> => {
  const mutation = useMutation<void, Error, number>({
    mutationFn: async (id: number) => {
      const amplifyApiConfig: IAmplifyApiConfig = {
        apiName: 'PacificApi',
        path: `${BASE_PATH}/${id}`,
        options: { body: { read: true } },
      };

      await amplifyApiService.put<
        { read: boolean },
        {
          message: string;
          data: boolean;
        }
      >(amplifyApiConfig);
    },
    ...options,
  });

  return mutation;
};
